import React, { useState, useEffect } from 'react'
import { CATEGORY_GROUPS, CATEGORY_GROUP_ORDER } from '../../utils/constants'
import { theme } from './warehouseTheme'

/**
 * The warehouse category picker: every main category with its subs, from the
 * master list (shared/categories). Testing, Manual Listing and the Receiving
 * and Testing split forms all use this one component. A pick returns the sub
 * and the main it was picked under: `onSelect(sub, main)`.
 */

/** The master list in the picker's shape: `{ groups: [{ bucket, productTypes }] }`. */
export function masterProductTypes() {
  return {
    groups: CATEGORY_GROUP_ORDER.map(main => ({
      bucket: main,
      productTypes: [...(CATEGORY_GROUPS[main] || [])].sort()
    }))
  }
}

/** The mains a sub sits under in `groups` (a shared sub sits under several). */
export function getMainCategoriesFromGroups(productType, groups) {
  if (!productType || !groups) return []
  return groups.filter(group => group.productTypes.includes(productType)).map(group => group.bucket)
}

function ProductTypeSelector({ productTypes, onSelect, onCancel, loading, currentSelection, accent = theme.colors.crimson }) {
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedBuckets, setExpandedBuckets] = useState({})

  // Auto-expand category containing current selection
  useEffect(() => {
    if (currentSelection && productTypes?.groups) {
      const mainCats = getMainCategoriesFromGroups(currentSelection, productTypes.groups)
      if (mainCats.length > 0) {
        const expanded = {}
        mainCats.forEach(cat => { expanded[cat] = true })
        setExpandedBuckets(expanded)
      }
    }
  }, [currentSelection, productTypes])

  const toggleBucket = (bucket) => {
    setExpandedBuckets(prev => ({ ...prev, [bucket]: !prev[bucket] }))
  }

  const filteredGroups = productTypes?.groups?.map(group => {
    if (!searchTerm) return group
    const filtered = group.productTypes.filter(type =>
      type.toLowerCase().includes(searchTerm.toLowerCase())
    )
    return filtered.length > 0 ? { ...group, productTypes: filtered } : null
  }).filter(Boolean) || []

  useEffect(() => {
    if (searchTerm) {
      const expanded = {}
      filteredGroups.forEach(g => { expanded[g.bucket] = true })
      setExpandedBuckets(expanded)
    }
  }, [searchTerm])

  return (
    <div style={{
      background: theme.colors.white, borderRadius: theme.radius.lg, boxShadow: theme.shadows.sm,
      border: `2px solid ${accent}`, padding: '24px', marginBottom: '20px'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h4 style={{ fontWeight: 600, color: accent, margin: 0 }}>Select Product Type</h4>
        <button
          type="button"
          onClick={onCancel}
          style={{ background: 'transparent', border: 'none', color: theme.colors.grey500, fontWeight: 600, fontSize: '13px', padding: '8px 14px', cursor: 'pointer' }}
        >
          Cancel
        </button>
      </div>

      <input
        type="text"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder="Search product types..."
        style={{
          width: '100%', padding: '12px 16px',
          border: `1px solid ${theme.colors.grey300}`,
          borderRadius: theme.radius.md, fontSize: '14px',
          marginBottom: '12px', outline: 'none'
        }}
      />

      {currentSelection && (
        <div style={{
          background: theme.colors.purpleLight,
          border: `1px solid ${theme.colors.purple}`,
          borderRadius: theme.radius.md,
          padding: '10px 14px',
          marginBottom: '16px',
          fontSize: '13px',
          color: theme.colors.purple,
          fontWeight: 500
        }}>
          Current: {getMainCategoriesFromGroups(currentSelection, productTypes?.groups).join(', ') || 'Unknown'} → {currentSelection}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px', color: theme.colors.grey500 }}>Loading…</div>
      ) : (
        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {filteredGroups.map(group => (
            <div key={group.bucket} style={{ marginBottom: '8px' }}>
              <button
                type="button"
                onClick={() => toggleBucket(group.bucket)}
                style={{
                  width: '100%', padding: '12px 16px',
                  background: theme.colors.grey100, border: 'none',
                  borderRadius: theme.radius.md, cursor: 'pointer',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  fontWeight: 600, fontSize: '14px'
                }}
              >
                <span>{group.bucket}</span>
                <span style={{ color: theme.colors.grey500 }}>
                  {expandedBuckets[group.bucket] ? '▼' : '▶'} ({group.productTypes.length})
                </span>
              </button>

              {expandedBuckets[group.bucket] && (
                <div style={{ padding: '8px 0 8px 16px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {group.productTypes.map(type => {
                    const isSelected = type === currentSelection
                    return (
                      <button
                        type="button"
                        key={type}
                        onClick={() => onSelect(type, group.bucket)}
                        style={{
                          padding: '8px 14px',
                          border: `1px solid ${isSelected ? theme.colors.purple : theme.colors.grey300}`,
                          borderRadius: theme.radius.md,
                          background: isSelected ? theme.colors.purpleLight : theme.colors.white,
                          cursor: 'pointer', fontSize: '13px',
                          fontWeight: isSelected ? 600 : 400
                        }}
                      >
                        {type}
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default ProductTypeSelector
